import { useEffect, useMemo, useRef, useState } from 'react';

import {
  AgentOperationDtoStatus,
  CalendarMeetingDto,
  MeetingTemplateDto,
  useAnalyzeMeeting,
  useListAgentOperations,
  useListMeetingAnalysis,
  useListMeetingsTemplates,
  getListMeetingsTemplatesQueryKey
} from '@/api/generated';
import { useMeetingAnalyzeContext } from '@/context/MeetingAnalyzeContextProvider/useMeetingAnalyzeContext';

export const useMeetingAgentsTemplate = ({
  meeting,
  defaultTemplate
}: {
  meeting: CalendarMeetingDto | undefined;
  defaultTemplate: MeetingTemplateDto;
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<MeetingTemplateDto | null>(null);
  const [isAnalysisFailed, setIsAnalysisFailed] = useState(false);
  const { meetingsAnalyzeStatus, setMeetingsAnalyzeStatus } = useMeetingAnalyzeContext();
  const isAnalyzing = !!meetingsAnalyzeStatus[meeting?.id || ''];
  const analyzeOperationId = useRef<string | undefined>(undefined);
  const { mutateAsync: analyzeMeeting } = useAnalyzeMeeting();
  const { data: agentOperationsList } = useListAgentOperations(
    { sourceId: meeting?.id },
    { query: { refetchInterval: 2000, enabled: isAnalyzing } }
  );
  const { data: agentTemplates } = useListMeetingsTemplates({
    query: {
      enabled: !!meeting?.id,
      queryKey: getListMeetingsTemplatesQueryKey()
    }
  });

  const {
    data: meetingAnalysisResponse,
    refetch: refetchMeetingAnalysis,
    isLoading: isLoadingMeetingAnalysis,
    isFetching: isFetchingMeetingAnalysis
  } = useListMeetingAnalysis(meeting?.id || '', {
    query: {
      enabled: !!meeting?.id,
      refetchOnWindowFocus: false
    }
  });
  useEffect(() => {
    if (!agentTemplates || !meetingAnalysisResponse || selectedTemplate) return;
    const template = agentTemplates.items?.find(
      template => template.id === meetingAnalysisResponse?.items?.at(0)?.meetingTemplateId
    );
    if (!template) {
      setSelectedTemplate(defaultTemplate);
      return;
    }
    setSelectedTemplate(template);
  }, [agentTemplates, meetingAnalysisResponse, selectedTemplate, defaultTemplate]);

  useEffect(() => {
    if (!agentOperationsList || !isAnalyzing) return;
    const currentOperation = agentOperationsList.items?.find(
      operation => operation.id === analyzeOperationId.current
    );
    const isAnalysisCompleted = currentOperation?.status === AgentOperationDtoStatus.finished;

    if (isAnalysisCompleted) {
      analyzeOperationId.current = undefined;
      setMeetingsAnalyzeStatus?.(prev => {
        const meetingsState = { ...prev };
        delete meetingsState[meeting?.id || ''];
        return meetingsState;
      });
      refetchMeetingAnalysis();
    }
    setIsAnalysisFailed(currentOperation?.status === AgentOperationDtoStatus.failed);
  }, [agentOperationsList, isAnalyzing, meeting?.id, analyzeOperationId, refetchMeetingAnalysis]);

  const isDefaultTemplate = selectedTemplate?.id === defaultTemplate.id;

  const sections = useMemo(() => {
    if (isDefaultTemplate) return [];
    const analysisSections = meetingAnalysisResponse?.items?.find(
      item => item.meetingTemplateId === selectedTemplate?.id
    )?.sections;
    if (!analysisSections) return [];

    return selectedTemplate?.sections?.map(section => ({
      ...section,
      content:
        analysisSections.find(item => item.title?.includes(section.title || ''))?.content || ''
    }));
  }, [meetingAnalysisResponse, selectedTemplate, isDefaultTemplate]);

  const userAgentActiveTemplate = useMemo(
    () => (agentTemplates?.items ? [defaultTemplate, ...agentTemplates.items] : [defaultTemplate]),
    [agentTemplates, defaultTemplate]
  );

  const onTemplateClick = async (template: MeetingTemplateDto | null) => {
    if (!template) return;
    setIsAnalysisFailed(false);

    refetchMeetingAnalysis();

    setSelectedTemplate(template);
    const isAnalysisExists = meetingAnalysisResponse?.items?.find(
      item => item.meetingTemplateId === template.id
    );
    if (template.id === defaultTemplate.id || isAnalysisExists) return;

    setMeetingsAnalyzeStatus?.(prev => ({ ...prev, [meeting?.id || '']: true }));
    const response = await analyzeMeeting({
      data: {
        bucketName: meeting?.transcript?.bucketName,
        fileKey: meeting?.transcript?.fileKey,
        meetingId: meeting?.id,
        template: {
          templateId: template.id,
          context: template.context
        },
        calendarId: meeting?.calendarEventId
      }
    });
    analyzeOperationId.current = response.items?.at(0)?.id;
  };

  return {
    meetingAnalysis: meetingAnalysisResponse?.items?.at(0),
    selectedTemplate,
    setSelectedTemplate,
    sections,
    isAnalyzing,
    isLoadingMeetingAnalysis: isLoadingMeetingAnalysis || isFetchingMeetingAnalysis,
    userAgentActiveTemplate,
    isDefaultTemplate,
    onTemplateClick,
    isAnalysisFailed
  };
};
